import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '@core/auth/auth.service';
import { AUTH_TOKEN_CACHE_KEY } from '@core/constants/auth.constants';
import { isUsersLoginRequest, isUsersRefreshRequest } from '@core/constants/users-api.constants';
import { CacheService } from '@core/services/cache.service';
import { LanguageService } from '@core/services/language.service';
import { catchError, switchMap, throwError } from 'rxjs';

export function AuthHttpInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) {
  const cacheService = inject(CacheService);
  const languageService = inject(LanguageService);
  const authService = inject(AuthService);
  const jwt = cacheService.getItem<string>(AUTH_TOKEN_CACHE_KEY);

  const isTranslationRequest = req.url.includes('i18n');
  const isRefreshRequest = isUsersRefreshRequest(req.url);
  const isLoginRequest = isUsersLoginRequest(req.url);

  if (jwt && !isTranslationRequest && !isRefreshRequest && !isLoginRequest) {
    const authRequest = req.clone({
      setHeaders: {
        authorization: `Bearer ${jwt}`,
        'Accept-Language': languageService.activeLanguage(),
      },
    });

    return next(authRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 401 || isRefreshRequest) {
          return throwError(() => error);
        }
        return authService.refreshToken().pipe(
          switchMap(() => {
            const newToken = authService.getAccessToken();
            const retryRequest = req.clone({
              setHeaders: {
                authorization: `Bearer ${newToken}`,
                'Accept-Language': languageService.activeLanguage(),
              },
            });
            return next(retryRequest);
          }),
          catchError((err) => throwError(() => err)),
        );
      }),
    );
  }

  return next(req);
}
